/**
 * Keeping the access token alive for as long as the tab is (ADR 318).
 *
 * The shell used to take the token once, at boot, and never touch it again:
 * `keycloak.init()` resolved, the raw JWT was copied into the site store and
 * mirrored onto `window.__SITE_STORE_TOKEN`, and every later request read that
 * one string. keycloak-js does NOT refresh on its own — it only refreshes when
 * something calls `updateToken()` — so at `accessTokenLifespan` (900 s in
 * `infra/keycloak/hunter-twin-realm.json`) plus the resource server's 60 s clock
 * skew, every call started coming back
 *
 *     401 — Bearer error="invalid_token", "Jwt expired at …"
 *
 * and stayed that way until the page was reloaded, which re-ran the handshake
 * against the still-valid SSO session (idle 1800 s) and silently produced a new
 * token. That is exactly the reported shape: a tab left open, then a site switch
 * showing "Nenhum depósito ainda", fixed by F5.
 *
 * So: refresh on a timer while the page is visible. Visibility is the gate on
 * purpose — a hidden tab stops refreshing and its SSO session is allowed to idle
 * out exactly as it does today, which is the whole point of `ssoSessionIdleTimeout`.
 * A visible dashboard on a wall is the case that must never expire.
 */

/** The slice of keycloak-js this module needs — narrow enough to fake in a test. */
export interface RefreshableSession {
  /** Refreshes if the token expires within `minValidity` seconds. True = refreshed. */
  updateToken(minValidity: number): Promise<boolean>;
  token?: string;
}

/**
 * Refresh when less than this much life is left. It must exceed CHECK_EVERY_MS
 * comfortably: a token may not be allowed to reach expiry between two ticks, or
 * a request lands in the gap and 401s — the bug this module exists to remove.
 */
export const MIN_VALIDITY_S = 180;
/** How often the check runs while the page is visible. */
export const CHECK_EVERY_MS = 60_000;

export interface TokenRefreshOptions {
  /** Called with the new raw JWT whenever one was actually issued. */
  onToken: (raw: string) => void;
  /**
   * The refresh token itself is gone (SSO session idled out, max lifespan
   * reached, or the session was ended elsewhere). There is no token to be had
   * without the user, so the caller re-runs the login redirect.
   */
  onSessionEnded: (error: unknown) => void;
}

/**
 * Start the refresh loop. Returns a stop function (tests, hot reload).
 *
 * Both wake-ups matter: the interval covers a tab that simply stays open, and
 * `visibilitychange`/`focus` cover the case timers cannot — a suspended laptop,
 * where the interval never fires while asleep and the token is long dead by the
 * time the lid opens.
 */
export function startTokenRefresh(
  session: RefreshableSession,
  { onToken, onSessionEnded }: TokenRefreshOptions,
): () => void {
  let stopped = false;
  let inFlight = false;

  const isVisible = () =>
    typeof document === "undefined" || document.visibilityState !== "hidden";

  async function tick(): Promise<void> {
    // Overlapping ticks would race two refreshes against one refresh token; on a
    // rotating-refresh-token realm the loser invalidates the winner.
    if (stopped || inFlight || !isVisible()) return;
    inFlight = true;
    try {
      const refreshed = await session.updateToken(MIN_VALIDITY_S);
      if (refreshed && session.token) onToken(session.token);
    } catch (e) {
      onSessionEnded(e);
    } finally {
      inFlight = false;
    }
  }

  const onWake = () => { void tick(); };
  const timer = setInterval(onWake, CHECK_EVERY_MS);
  if (typeof document !== "undefined") document.addEventListener("visibilitychange", onWake);
  if (typeof window !== "undefined") window.addEventListener("focus", onWake);

  // Run once now: a reload lands here with whatever life the handshake produced.
  void tick();

  return () => {
    stopped = true;
    clearInterval(timer);
    if (typeof document !== "undefined") document.removeEventListener("visibilitychange", onWake);
    if (typeof window !== "undefined") window.removeEventListener("focus", onWake);
  };
}
